import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { api } from '../scripts/api';

const STATUSES = [
  { key: 'ok',       label: 'OK',       bg: '#16a34a', fg: '#fff' },
  { key: 'not_ok',   label: 'Not OK',   bg: '#dc2626', fg: '#fff' },
  { key: 'repaired', label: 'Repaired', bg: '#d97706', fg: '#fff' },
  { key: 'na',       label: 'N/A',      bg: '#6b7280', fg: '#fff' },
];

const SEV_COLOR = { minor: '#d97706', major: '#dc2626', safety: '#7c3aed' };
const CANDIDATE_TABLES = ['all_inventory', 'stock_inventory', 'used_inventory', 'sold_inventory'];

// ─── full PDI booklet (print/PDF) ────────────────────────────────────────────
async function generatePdiBooklet(sessionId, apiGet) {
  let data;
  try {
    const res = await apiGet(`/pdi/sessions/${sessionId}/full-report`);
    data = res.data;
  } catch (e) {
    alert('Failed to load report data: ' + (e?.response?.data?.error || e.message));
    return;
  }

  const { session, template, responses, deficiencies, inventory } = data;
  const respMap = {};
  (responses || []).forEach(r => { respMap[r.step_id] = r; });

  const STATUS_LABEL = { ok: 'OK', not_ok: 'Not OK', repaired: 'Repaired', na: 'N/A' };
  const STATUS_COLOR = { ok: '#16a34a', not_ok: '#dc2626', repaired: '#d97706', na: '#6b7280' };
  const SEV_COL      = { minor: '#d97706', major: '#dc2626', safety: '#7c3aed' };

  const baseUrl = window.location.origin;

  const checkCell = (key, status) => {
    const active = status === key;
    return `<td style="text-align:center;border:1px solid #cbd5e1;padding:6px 8px;background:${active ? STATUS_COLOR[key] : '#fff'};color:${active ? '#fff' : '#94a3b8'};font-weight:${active ? '800' : '400'};font-size:12px;min-width:60px">${active ? '&#10003; ' : ''}${STATUS_LABEL[key]}</td>`;
  };

  const allSteps = (template?.sections || []).flatMap(s => s.steps.map(st => ({ ...st, sectionTitle: s.title })));
  const defMap = {};
  (deficiencies || []).forEach(d => {
    if (d.step_id) { (defMap[d.step_id] = defMap[d.step_id] || []).push(d); }
  });

  // ── section rows ────────────────────────────────────────────────────────────
  let sectionsHtml = '';
  for (const sec of (template?.sections || [])) {
    sectionsHtml += `
      <tr><td colspan="6" style="background:#0f172a;color:#fff;font-weight:800;font-size:13px;padding:8px 10px;letter-spacing:0.5px">${sec.title}</td></tr>`;
    for (const step of sec.steps) {
      const r = respMap[step.id] || {};
      const stepDefs = defMap[step.id] || [];
      const defBadge = stepDefs.length
        ? `<div style="margin-top:4px">${stepDefs.map(d =>
            `<span style="font-size:10px;background:${d.status==='resolved'?'#d1fae5':'#fee2e2'};color:${d.status==='resolved'?'#065f46':'#991b1b'};border-radius:4px;padding:1px 5px;margin-right:3px">${d.status==='resolved'?'&#10003;':'!'} ${d.description.slice(0,50)}</span>`
          ).join('')}</div>` : '';
      const photoLink = r.photo_path ? `<a href="${baseUrl}/api${r.photo_path}" style="font-size:10px;color:#3b82f6" target="_blank">Photo</a>` : '';

      sectionsHtml += `
        <tr style="page-break-inside:avoid">
          <td style="border:1px solid #e2e8f0;padding:8px 10px;font-size:12px;font-weight:700;white-space:nowrap;vertical-align:top">${step.id}</td>
          <td style="border:1px solid #e2e8f0;padding:8px 10px;font-size:12px;vertical-align:top">
            <div style="font-weight:700">${step.item}</div>
            ${step.instruction ? `<div style="color:#64748b;font-size:11px;margin-top:2px">${step.instruction}</div>` : ''}
            ${defBadge}
          </td>
          ${checkCell('ok', r.status)}
          ${checkCell('not_ok', r.status)}
          ${checkCell('repaired', r.status)}
          ${checkCell('na', r.status)}
          <td style="border:1px solid #e2e8f0;padding:6px 8px;font-size:11px;color:#374151;vertical-align:top;min-width:100px">
            ${r.comment ? r.comment : '<span style="color:#cbd5e1">—</span>'}
            ${photoLink}
          </td>
        </tr>`;
    }
  }

  // ── deficiency table ────────────────────────────────────────────────────────
  const allDefs = deficiencies || [];
  const defRows = allDefs.length
    ? allDefs.map(d => `
        <tr>
          <td style="border:1px solid #e2e8f0;padding:6px 8px;font-weight:700;font-size:12px;color:${SEV_COL[d.severity]||'#333'};text-transform:uppercase">${d.severity}</td>
          <td style="border:1px solid #e2e8f0;padding:6px 8px;font-size:12px">${d.step_id ? 'Step ' + d.step_id : 'General'}</td>
          <td style="border:1px solid #e2e8f0;padding:6px 8px;font-size:12px">${d.description}</td>
          <td style="border:1px solid #e2e8f0;padding:6px 8px;font-size:12px;color:${d.status==='resolved'?'#16a34a':'#dc2626'};font-weight:700">${d.status.toUpperCase()}</td>
          <td style="border:1px solid #e2e8f0;padding:6px 8px;font-size:12px">${d.resolution_note || '—'}</td>
        </tr>`).join('')
    : `<tr><td colspan="5" style="padding:12px;color:#16a34a;font-weight:700;text-align:center">No deficiencies recorded</td></tr>`;

  const sigHtml = session.signature_path
    ? `<img src="${baseUrl}/api${session.signature_path}" style="max-height:100px;border:1px solid #e2e8f0;border-radius:4px" alt="Engineering signature" />`
    : `<div style="width:260px;height:80px;border:1px dashed #94a3b8;border-radius:4px;display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:12px">Signature not captured</div>`;

  const inv = inventory || {};
  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<title>PDI — ${template?.title || 'Inspection'} — ${inv.rowo || session.inventory_id}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;color:#111;background:#fff;font-size:13px}
  .page{padding:28px 32px;page-break-after:always}
  .page:last-child{page-break-after:auto}
  h1{font-size:22px;font-weight:900;letter-spacing:0.3px}
  h2{font-size:15px;font-weight:800;margin-bottom:10px;color:#0f172a}
  .meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px 20px;margin:16px 0}
  .meta-item label{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;display:block}
  .meta-item span{font-weight:700;font-size:13px}
  table{width:100%;border-collapse:collapse}
  th{background:#1e293b;color:#fff;padding:7px 10px;text-align:left;font-size:12px}
  .section-break{page-break-before:auto}
  @media print{
    .page{page-break-after:always}
    .page:last-child{page-break-after:auto}
    a{color:#111!important}
    body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  }
</style>
</head><body>

<!-- COVER PAGE -->
<div class="page">
  <div style="border-bottom:4px solid #0f172a;padding-bottom:16px;margin-bottom:20px">
    <div style="font-size:11px;color:#64748b;letter-spacing:1px;text-transform:uppercase;margin-bottom:4px">SNA Equipment</div>
    <h1>Pre-Delivery Inspection</h1>
    <div style="font-size:16px;font-weight:700;color:#334155;margin-top:4px">${template?.title || ''}</div>
    ${template?.version ? `<div style="font-size:11px;color:#94a3b8;margin-top:2px">Version ${template.version}</div>` : ''}
  </div>

  <div class="meta-grid">
    <div class="meta-item"><label>RO / WO</label><span>${inv.rowo || '—'}</span></div>
    <div class="meta-item"><label>Session ID</label><span>${session.id}</span></div>
    <div class="meta-item"><label>Body VIN</label><span>${inv.body_vin || '—'}</span></div>
    <div class="meta-item"><label>Chassis VIN</label><span>${inv.chassis_vin || '—'}</span></div>
    <div class="meta-item"><label>Customer</label><span>${inv.customer || '—'}</span></div>
    <div class="meta-item"><label>Stock / PO</label><span>${inv.stock_po || '—'}</span></div>
    <div class="meta-item"><label>Unit Year</label><span>${inv.year || '—'}</span></div>
    <div class="meta-item"><label>Unit Status</label><span>${inv.unit_status || '—'}</span></div>
  </div>

  <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-top:20px;border-top:1px solid #e2e8f0;padding-top:16px">
    <div class="meta-item"><label>PDI Started</label><span>${(session.started_at || '').slice(0,16).replace('T',' ')}</span></div>
    <div class="meta-item"><label>PDI Completed</label><span>${(session.completed_at || '').slice(0,16).replace('T',' ') || '—'}</span></div>
    <div class="meta-item"><label>Status</label><span style="color:${session.status==='completed'?'#16a34a':'#d97706'};text-transform:uppercase">${session.status}</span></div>
    <div class="meta-item"><label>Tech Initials</label><span>${session.tech_initials || '—'}</span></div>
    <div class="meta-item"><label>Engineering Initials</label><span>${session.engineering_initials || '—'}</span></div>
    <div class="meta-item"><label>Eng Signed</label><span>${(session.engineering_signed_at || '').slice(0,16).replace('T',' ') || '—'}</span></div>
  </div>

  <div style="margin-top:28px;border:1px solid #e2e8f0;border-radius:6px;padding:14px">
    <div style="font-size:11px;color:#94a3b8;margin-bottom:8px;font-weight:700">TABLE OF CONTENTS</div>
    <ol style="margin-left:18px;line-height:2">
      ${(template?.sections || []).map(s => `<li>${s.title}</li>`).join('')}
      <li>Deficiency Summary</li>
      <li>Engineering Sign-Off</li>
    </ol>
  </div>
</div>

<!-- CHECKLIST PAGE -->
<div class="page">
  <h2>Inspection Checklist</h2>
  <table>
    <thead>
      <tr>
        <th style="width:40px">#</th>
        <th>Item / Instruction</th>
        <th style="width:65px;text-align:center">OK</th>
        <th style="width:65px;text-align:center">Not OK</th>
        <th style="width:70px;text-align:center">Repaired</th>
        <th style="width:50px;text-align:center">N/A</th>
        <th style="width:130px">Comments</th>
      </tr>
    </thead>
    <tbody>
      ${sectionsHtml}
    </tbody>
  </table>
</div>

<!-- DEFICIENCY PAGE -->
<div class="page">
  <h2>Deficiency Summary</h2>
  <table>
    <thead>
      <tr>
        <th style="width:70px">Severity</th>
        <th style="width:70px">Step</th>
        <th>Description</th>
        <th style="width:80px">Status</th>
        <th>Resolution</th>
      </tr>
    </thead>
    <tbody>${defRows}</tbody>
  </table>
</div>

<!-- SIGN-OFF PAGE -->
<div class="page">
  <h2>Engineering Sign-Off</h2>
  <div style="margin-bottom:24px">
    <p style="font-size:13px;color:#475569;line-height:1.6">I confirm that this Pre-Delivery Inspection has been completed in accordance with SNA Equipment standards and all identified deficiencies have been addressed.</p>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:24px">
    <div>
      <div style="font-size:11px;color:#64748b;text-transform:uppercase;margin-bottom:8px;font-weight:700">Technician</div>
      <div style="border-bottom:1px solid #0f172a;padding-bottom:4px;min-width:200px;margin-bottom:4px;font-size:15px;font-weight:700">${session.tech_initials || ''}</div>
      <div style="font-size:11px;color:#94a3b8">Initials / Date: ${(session.completed_at || '').slice(0,10)}</div>
    </div>
    <div>
      <div style="font-size:11px;color:#64748b;text-transform:uppercase;margin-bottom:8px;font-weight:700">Engineering Sign-Off</div>
      ${sigHtml}
      <div style="font-size:11px;color:#94a3b8;margin-top:6px">Initials: ${session.engineering_initials || '—'}  |  Date: ${(session.engineering_signed_at || '').slice(0,10) || '—'}</div>
    </div>
  </div>
  <div style="margin-top:48px;border-top:1px solid #e2e8f0;padding-top:12px;font-size:10px;color:#94a3b8;display:flex;justify-content:space-between">
    <span>SNA Equipment — PDI Record</span>
    <span>Session #${session.id} — Generated ${new Date().toLocaleString()}</span>
  </div>
</div>

<script>window.onload = () => window.print()</script>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) { alert('Pop-up blocked. Please allow pop-ups for this site.'); return; }
  w.document.write(html);
  w.document.close();
}

// ─── print deficiency report ──────────────────────────────────────────────────
function printDeficiencyReport(inventory, template, deficiencies, sessionId) {
  const unit = [
    inventory?.rowo && `RO/WO: ${inventory.rowo}`,
    inventory?.body_vin && `VIN: ${inventory.body_vin}`,
    inventory?.chassis_vin && `Chassis VIN: ${inventory.chassis_vin}`,
  ].filter(Boolean).join('  |  ');

  const rows = deficiencies.map((d) => `
    <tr>
      <td style="padding:8px;border:1px solid #ddd;font-weight:700;color:${SEV_COLOR[d.severity] || '#333'};text-transform:uppercase">${d.severity}</td>
      <td style="padding:8px;border:1px solid #ddd">${d.step_id ? `Step ${d.step_id}` : 'General'}</td>
      <td style="padding:8px;border:1px solid #ddd">${d.description}</td>
      <td style="padding:8px;border:1px solid #ddd;color:${d.status === 'resolved' ? '#16a34a' : '#dc2626'};font-weight:700">${d.status.toUpperCase()}</td>
      <td style="padding:8px;border:1px solid #ddd">${d.resolution_note || ''}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html><html><head><title>Deficiency Report — Session ${sessionId}</title>
  <style>body{font-family:Arial,sans-serif;padding:32px;color:#111}h1{font-size:22px;margin-bottom:4px}
  .meta{color:#555;margin-bottom:24px;font-size:14px}
  table{width:100%;border-collapse:collapse;font-size:13px}
  th{background:#1e293b;color:#fff;padding:8px;text-align:left}
  tr:nth-child(even) td{background:#f8fafc}
  .open{color:#dc2626}.resolved{color:#16a34a}
  @media print{body{padding:16px}}</style></head><body>
  <h1>PDI Deficiency Report</h1>
  <div class="meta">${unit}${unit ? '<br>' : ''}Template: ${template?.title || ''} &nbsp;|&nbsp; Session: ${sessionId} &nbsp;|&nbsp; Generated: ${new Date().toLocaleString()}</div>
  ${deficiencies.length === 0
    ? '<p style="color:#16a34a;font-weight:bold">No deficiencies recorded.</p>'
    : `<table><thead><tr><th>Severity</th><th>Step</th><th>Description</th><th>Status</th><th>Resolution</th></tr></thead><tbody>${rows}</tbody></table>`
  }
  <script>window.onload=()=>window.print()</script>
  </body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
}

// ─── main component ───────────────────────────────────────────────────────────
export default function PdiPage() {
  const { id } = useParams();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const queryTable    = qs.get('table');
  const queryTemplate = qs.get('template');

  const [inventory,      setInventory]      = useState(null);
  const [table,          setTable]          = useState(null);
  const [template,       setTemplate]       = useState(null);
  const [sessionId,      setSessionId]      = useState(null);
  const [resumed,        setResumed]        = useState(false);
  const [responses,      setResponses]      = useState({});
  const [busy,           setBusy]           = useState(false);
  const [msg,            setMsg]            = useState('');
  const [sessionStatus,  setSessionStatus]  = useState('in_progress');
  const [techInitials,   setTechInitials]   = useState('');
  const [engInitials,    setEngInitials]    = useState('');
  const [signDataUrl,    setSignDataUrl]    = useState('');
  const [signMsg,        setSignMsg]        = useState('');

  // navigation
  const [currentIdx,  setCurrentIdx]  = useState(0);
  const [showToc,     setShowToc]     = useState(false);
  const [showDefs,    setShowDefs]    = useState(false);
  const [showFinish,  setShowFinish]  = useState(false);

  // deficiencies
  const [deficiencies, setDeficiencies] = useState([]);
  const [defDesc,      setDefDesc]      = useState('');
  const [defSeverity,  setDefSeverity]  = useState('minor');
  const [defStepId,    setDefStepId]    = useState(null);
  const [defBusy,      setDefBusy]      = useState(false);

  const advanceTimer = useRef(null);

  // ── load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setBusy(true);
        let found = null; let foundTable = null;
        const tables = queryTable
          ? [queryTable, ...CANDIDATE_TABLES.filter(t => t !== queryTable)]
          : CANDIDATE_TABLES;
        for (const t of tables) {
          try { const r = await api.get(`/${t}/${id}`); found = r.data; foundTable = t; break; }
          catch (_) {}
        }
        if (!found) { setMsg(`Record ${id} not found.`); return; }
        setInventory(found);
        setTable(foundTable);

        const { data: tpl } = await api.get(`/pdi/templates/${queryTemplate || 'tana_compactor'}`);
        setTemplate(tpl);

        const { data: session } = await api.post('/pdi/sessions', {
          inventory_id: id, table: foundTable, template: tpl.template,
          meta: { vin: found?.body_vin || found?.chassis_vin || '' },
        });
        setSessionId(session.session_id);
        setResumed(!!session.resumed);
      } catch (e) {
        setMsg(`Load failed: ${e.response?.data?.error || e.message}`);
      } finally {
        setBusy(false);
      }
    };
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── load responses + deficiencies ──────────────────────────────────────────
  useEffect(() => {
    if (!sessionId || !template) return;
    const fetch = async () => {
      const { data } = await api.get(`/pdi/sessions/${sessionId}`);
      const map = {};
      (data.responses || []).forEach(r => {
        map[r.step_id] = { status: r.status, comment: r.comment || '', photo: r.photo_path || '' };
      });
      setResponses(map);
      if (data.session?.status)              setSessionStatus(data.session.status);
      if (data.session?.tech_initials)       setTechInitials(data.session.tech_initials);
      if (data.session?.engineering_initials) setEngInitials(data.session.engineering_initials);
      if (data.session?.signature_path)      setSignDataUrl(data.session.signature_path);

      const { data: defs } = await api.get(`/pdi/sessions/${sessionId}/deficiencies`);
      setDeficiencies(defs.deficiencies || []);

      // jump to first unanswered step on resume
      if (data.session?.status === 'in_progress') {
        const allStepsFlat = template.sections.flatMap(s => s.steps);
        const firstUnanswered = allStepsFlat.findIndex(s => !map[s.id]);
        if (firstUnanswered >= 0) setCurrentIdx(firstUnanswered);
      }
    };
    fetch();
  }, [sessionId, template]);

  // ── flatten steps ───────────────────────────────────────────────────────────
  const allSteps = useMemo(() => {
    if (!template) return [];
    return template.sections.flatMap(s => s.steps.map(step => ({ ...step, sectionTitle: s.title })));
  }, [template]);

  const currentStep = allSteps[currentIdx] || null;
  const total       = allSteps.length;
  const answered    = Object.keys(responses).length;
  const pct         = total > 0 ? Math.round((answered / total) * 100) : 0;
  const openDefs    = deficiencies.filter(d => d.status === 'open');
  const allDone     = answered >= total;
  const canComplete = allDone && openDefs.length === 0 && techInitials.trim();

  // ── step handlers ───────────────────────────────────────────────────────────
  const handleStatus = async (stepId, status) => {
    if (!sessionId) return;
    setResponses(prev => ({ ...prev, [stepId]: { ...(prev[stepId] || {}), status } }));
    await api.post(`/pdi/sessions/${sessionId}/step`, {
      step_id: stepId, status, comment: responses[stepId]?.comment || '',
    });
    // auto-advance after short delay
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => {
      setCurrentIdx(i => Math.min(total - 1, i + 1));
    }, 400);
  };

  const handleComment = async (stepId, comment) => {
    if (!sessionId) return;
    setResponses(prev => ({ ...prev, [stepId]: { ...(prev[stepId] || {}), comment } }));
    await api.post(`/pdi/sessions/${sessionId}/step`, {
      step_id: stepId, status: responses[stepId]?.status || 'not_ok', comment,
    });
  };

  const uploadStepPhoto = async (stepId, file) => {
    if (!sessionId || !file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('step_id', stepId);
    const { data } = await api.post(`/pdi/sessions/${sessionId}/photo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setResponses(prev => ({
      ...prev,
      [stepId]: { ...(prev[stepId] || {}), photo: data.url, status: prev[stepId]?.status || 'na' },
    }));
  };

  // ── deficiency handlers ─────────────────────────────────────────────────────
  const openDefModal = (stepId = null) => {
    setDefStepId(stepId);
    setDefDesc('');
    setDefSeverity('minor');
    setShowDefs(true);
  };

  const submitDeficiency = async () => {
    if (!defDesc.trim() || !sessionId) return;
    setDefBusy(true);
    try {
      await api.post(`/pdi/sessions/${sessionId}/deficiencies`, {
        description: defDesc.trim(), severity: defSeverity, step_id: defStepId,
      });
      const { data } = await api.get(`/pdi/sessions/${sessionId}/deficiencies`);
      setDeficiencies(data.deficiencies || []);
      setDefDesc('');
      setDefStepId(null);
    } catch (e) {
      setMsg('Failed to save deficiency');
    } finally {
      setDefBusy(false);
    }
  };

  const resolveDeficiency = async (defId, note) => {
    await api.patch(`/pdi/deficiencies/${defId}`, { status: 'resolved', resolution_note: note });
    setDeficiencies(prev => prev.map(d => d.id === defId ? { ...d, status: 'resolved', resolution_note: note } : d));
  };

  const uploadDefPhoto = async (defId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await api.post(`/pdi/deficiencies/${defId}/photo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setDeficiencies(prev => prev.map(d => d.id === defId ? { ...d, photo_path: data.url } : d));
  };

  // ── complete ────────────────────────────────────────────────────────────────
  const complete = async () => {
    if (!sessionId) return;
    try {
      await api.post(`/pdi/sessions/${sessionId}/complete`, { tech_initials: techInitials });
      setSessionStatus('completed');
    } catch (e) {
      setMsg(e.response?.data?.error || 'Complete failed');
    }
  };

  // ── signature canvas ────────────────────────────────────────────────────────
  const canvasId = 'pdi-sig';
  useEffect(() => {
    const canvas = document.getElementById(canvasId);
    if (!canvas || sessionStatus !== 'completed') return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2; ctx.lineCap = 'round';
    let drawing = false; let last = null;
    const pos = (ev) => {
      const rect = canvas.getBoundingClientRect();
      const p = ev.touches ? ev.touches[0] : ev;
      return { x: p.clientX - rect.left, y: p.clientY - rect.top };
    };
    const start = (ev) => { drawing = true; last = pos(ev); };
    const move  = (ev) => {
      if (!drawing || !last) return;
      const { x, y } = pos(ev);
      ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(x, y); ctx.stroke();
      last = { x, y };
    };
    const end = () => { drawing = false; last = null; };
    canvas.onmousedown = start; canvas.onmousemove = move; window.onmouseup = end;
    canvas.ontouchstart = (e) => { e.preventDefault(); start(e); };
    canvas.ontouchmove  = (e) => { e.preventDefault(); move(e); };
    canvas.ontouchend   = end;
  }, [sessionStatus, showFinish]);

  const clearSig = () => {
    const c = document.getElementById(canvasId);
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setSignDataUrl(''); setSignMsg('');
  };

  const saveSig = () => {
    if (!engInitials.trim()) { setSignMsg('Engineering initials required.'); return; }
    const c = document.getElementById(canvasId);
    c.toBlob(async (blob) => {
      const fd = new FormData();
      fd.append('file', blob, 'signature.png');
      fd.append('initials', engInitials.trim());
      try {
        const { data } = await api.post(`/pdi/sessions/${sessionId}/signature`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setSignDataUrl(data.url); setSignMsg('Signature saved.');
      } catch (e) { setSignMsg('Save failed.'); }
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  if (busy) return <div className="container"><p className="muted">Loading PDI…</p></div>;
  if (msg && !template) return <div className="container"><p style={{ color: '#dc2626' }}>{msg}</p></div>;

  const resp = currentStep ? (responses[currentStep.id] || {}) : {};
  const stepDefs = currentStep ? deficiencies.filter(d => String(d.step_id) === String(currentStep.id)) : [];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f1f5f9', fontFamily: 'system-ui, sans-serif' }}>

      {/* ── STICKY HEADER ───────────────────────────────────────────────────── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 200, background: '#0f172a', color: '#fff', padding: '10px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>
              {template?.title || 'PDI'} — #{id}
              {resumed && <span style={{ marginLeft: 8, fontSize: 11, background: '#334155', borderRadius: 4, padding: '2px 6px' }}>Resumed</span>}
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              {[inventory?.rowo && `RO/WO ${inventory.rowo}`, inventory?.body_vin && `VIN ${inventory.body_vin}`].filter(Boolean).join('  ·  ')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Deficiency badge */}
            <button
              onClick={() => setShowDefs(v => !v)}
              style={{ position: 'relative', background: openDefs.length > 0 ? '#dc2626' : '#334155', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              Deficiencies
              {openDefs.length > 0 && (
                <span style={{ marginLeft: 6, background: '#fff', color: '#dc2626', borderRadius: 99, padding: '1px 6px', fontSize: 11, fontWeight: 900 }}>
                  {openDefs.length}
                </span>
              )}
            </button>
            {/* TOC toggle */}
            <button
              onClick={() => setShowToc(v => !v)}
              style={{ background: showToc ? '#3b82f6' : '#334155', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              Contents
            </button>
            {/* Finish */}
            <button
              onClick={() => setShowFinish(true)}
              style={{ background: allDone ? '#16a34a' : '#334155', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              Finish
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
            <span>{answered} / {total} steps answered</span>
            <span>{pct}%</span>
          </div>
          <div style={{ height: 6, background: '#1e293b', borderRadius: 99 }}>
            <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#16a34a' : '#3b82f6', borderRadius: 99, transition: 'width 0.3s' }} />
          </div>
        </div>
      </div>

      {/* ── TABLE OF CONTENTS ────────────────────────────────────────────────── */}
      {showToc && (
        <div style={{ background: '#1e293b', color: '#e2e8f0', padding: '12px 16px', overflowY: 'auto', maxHeight: '50vh' }}>
          {template?.sections.map((sec) => (
            <div key={sec.title} style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
                {sec.title}
              </div>
              {sec.steps.map((step) => {
                const r = responses[step.id];
                const idx = allSteps.findIndex(s => s.id === step.id);
                const isActive = idx === currentIdx;
                return (
                  <button
                    key={step.id}
                    onClick={() => { setCurrentIdx(idx); setShowToc(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: isActive ? '#334155' : 'transparent', color: '#e2e8f0', border: 'none', borderRadius: 6, padding: '7px 10px', textAlign: 'left', cursor: 'pointer', marginBottom: 2 }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: r?.status === 'ok' ? '#16a34a' : r?.status === 'not_ok' ? '#dc2626' : r?.status === 'repaired' ? '#d97706' : r?.status === 'na' ? '#6b7280' : '#475569' }} />
                    <span style={{ fontSize: 13 }}><strong>{step.id}.</strong> {step.item}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* ── DEFICIENCY PANEL ─────────────────────────────────────────────────── */}
      {showDefs && (
        <div style={{ background: '#fff', borderBottom: '2px solid #dc2626', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 800, fontSize: 16 }}>Deficiencies</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => printDeficiencyReport(inventory, template, deficiencies, sessionId)}
                style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
              >
                Print / PDF Report
              </button>
              <button onClick={() => setShowDefs(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 700 }}>Close</button>
            </div>
          </div>

          {/* Add new */}
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>
              {defStepId ? `Flag deficiency for Step ${defStepId}` : 'General deficiency'}
            </div>
            <textarea
              className="input"
              style={{ width: '100%', minHeight: 64, marginBottom: 8 }}
              placeholder="Describe the deficiency…"
              value={defDesc}
              onChange={e => setDefDesc(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select className="input" style={{ maxWidth: 160 }} value={defSeverity} onChange={e => setDefSeverity(e.target.value)}>
                <option value="minor">Minor</option>
                <option value="major">Major</option>
                <option value="safety">Safety</option>
              </select>
              <button
                onClick={submitDeficiency}
                disabled={defBusy || !defDesc.trim()}
                style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 700, cursor: 'pointer', opacity: (!defDesc.trim() || defBusy) ? 0.5 : 1 }}
              >
                Save Deficiency
              </button>
            </div>
          </div>

          {/* List */}
          {deficiencies.length === 0 && <div className="muted">No deficiencies recorded.</div>}
          <div style={{ display: 'grid', gap: 8 }}>
            {deficiencies.map(d => (
              <DefRow key={d.id} def={d} onResolve={resolveDeficiency} onPhoto={uploadDefPhoto} />
            ))}
          </div>
        </div>
      )}

      {/* ── STEP CARD ────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, padding: '16px 16px 0' }}>
        {currentStep ? (
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            {/* section + step label */}
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
              {currentStep.sectionTitle}
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', marginBottom: 6, lineHeight: 1.2 }}>
              {currentStep.id}. {currentStep.item}
            </div>
            {currentStep.instruction && (
              <div style={{ fontSize: 15, color: '#475569', marginBottom: 20, lineHeight: 1.5 }}>
                {currentStep.instruction}
              </div>
            )}

            {/* Status buttons — large touch targets */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {STATUSES.map(s => {
                const active = resp.status === s.key;
                return (
                  <button
                    key={s.key}
                    onClick={() => handleStatus(currentStep.id, s.key)}
                    style={{
                      padding: '18px 0',
                      fontSize: 17,
                      fontWeight: 800,
                      border: `3px solid ${s.bg}`,
                      borderRadius: 10,
                      background: active ? s.bg : '#fff',
                      color: active ? s.fg : s.bg,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {active && '✓ '}{s.label}
                  </button>
                );
              })}
            </div>

            {/* Comment */}
            <textarea
              className="input"
              style={{ width: '100%', minHeight: 72, marginBottom: 12, fontSize: 15 }}
              placeholder="Comments / measurements / initials…"
              value={resp.comment || ''}
              onChange={e => handleComment(currentStep.id, e.target.value)}
            />

            {/* Photo + flag deficiency */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                📷 Take / Upload Photo
                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                  onChange={e => e.target.files?.[0] && uploadStepPhoto(currentStep.id, e.target.files[0])} />
              </label>
              {resp.photo && (
                <a href={resp.photo.startsWith('http') ? resp.photo : `/api${resp.photo}`} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', fontWeight: 700, fontSize: 14, textDecoration: 'none', color: '#0f172a' }}>
                  View Photo
                </a>
              )}
              <button
                onClick={() => openDefModal(currentStep.id)}
                style={{ flex: 1, background: '#fff5f5', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 8, padding: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >
                ⚠ Flag Deficiency
              </button>
            </div>

            {/* Step deficiency badges */}
            {stepDefs.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {stepDefs.map(d => (
                  <span key={d.id} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 99, background: d.status === 'resolved' ? '#d1fae5' : '#fee2e2', color: d.status === 'resolved' ? '#065f46' : '#991b1b', fontWeight: 700 }}>
                    {d.status === 'resolved' ? '✓' : '!'} {d.description.slice(0, 50)}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="card" style={{ textAlign: 'center', color: '#94a3b8' }}>Select a step from Contents</div>
        )}
      </div>

      {/* ── BOTTOM NAV ───────────────────────────────────────────────────────── */}
      <div style={{ position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #e2e8f0', padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', zIndex: 100 }}>
        <button
          onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
          disabled={currentIdx === 0}
          style={{ flex: 1, padding: '14px 0', fontSize: 16, fontWeight: 800, background: currentIdx === 0 ? '#f1f5f9' : '#0f172a', color: currentIdx === 0 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 10, cursor: currentIdx === 0 ? 'default' : 'pointer' }}
        >
          ← Prev
        </button>
        <div style={{ textAlign: 'center', minWidth: 80 }}>
          <div style={{ fontWeight: 900, fontSize: 18 }}>{currentIdx + 1} / {total}</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{currentStep?.sectionTitle}</div>
        </div>
        <button
          onClick={() => setCurrentIdx(i => Math.min(total - 1, i + 1))}
          disabled={currentIdx === total - 1}
          style={{ flex: 1, padding: '14px 0', fontSize: 16, fontWeight: 800, background: currentIdx === total - 1 ? '#f1f5f9' : '#0f172a', color: currentIdx === total - 1 ? '#94a3b8' : '#fff', border: 'none', borderRadius: 10, cursor: currentIdx === total - 1 ? 'default' : 'pointer' }}
        >
          Next →
        </button>
      </div>

      {/* ── FINISH MODAL ─────────────────────────────────────────────────────── */}
      {showFinish && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ width: '100%', background: '#fff', borderRadius: '16px 16px 0 0', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontWeight: 900, fontSize: 20 }}>
                {sessionStatus === 'completed' ? '✓ PDI Completed' : 'Complete PDI'}
              </div>
              <button onClick={() => setShowFinish(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 700 }}>Close</button>
            </div>

            {msg && <div style={{ color: '#dc2626', marginBottom: 12 }}>{msg}</div>}

            {/* Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              {[['Steps Answered', `${answered} / ${total}`], ['Open Deficiencies', openDefs.length]].map(([label, val]) => (
                <div key={label} style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontWeight: 900, fontSize: 22 }}>{val}</div>
                </div>
              ))}
            </div>

            {openDefs.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: 12, marginBottom: 16, color: '#dc2626', fontWeight: 700 }}>
                ⚠ {openDefs.length} open deficiency/deficiencies must be resolved before completing.
              </div>
            )}

            {/* Deficiency report button */}
            <button
              onClick={() => printDeficiencyReport(inventory, template, deficiencies, sessionId)}
              style={{ width: '100%', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 0', fontWeight: 800, fontSize: 16, cursor: 'pointer', marginBottom: 12 }}
            >
              Print / Save Deficiency Report (PDF)
            </button>

            {/* Complete */}
            {sessionStatus === 'in_progress' && (
              <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                <input
                  className="input"
                  style={{ flex: 1, fontSize: 15 }}
                  placeholder="Tech initials"
                  value={techInitials}
                  onChange={e => setTechInitials(e.target.value)}
                />
                <button
                  onClick={complete}
                  disabled={!canComplete}
                  style={{ flex: 1, background: canComplete ? '#16a34a' : '#e2e8f0', color: canComplete ? '#fff' : '#94a3b8', border: 'none', borderRadius: 10, padding: '14px 0', fontWeight: 800, fontSize: 16, cursor: canComplete ? 'pointer' : 'default' }}
                >
                  Complete PDI
                </button>
              </div>
            )}

            {sessionStatus === 'completed' && (
              <div style={{ color: '#16a34a', fontWeight: 800, fontSize: 16, marginBottom: 20 }}>
                ✓ PDI Completed{techInitials && ` by ${techInitials}`}
              </div>
            )}

            {/* Engineering sign-off */}
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>Engineering Sign-Off</div>
              <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>Available after PDI is completed.</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <input
                  className="input"
                  style={{ flex: 1 }}
                  placeholder="Eng initials"
                  value={engInitials}
                  onChange={e => setEngInitials(e.target.value)}
                  disabled={sessionStatus !== 'completed'}
                />
                <button onClick={clearSig} disabled={sessionStatus !== 'completed'} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer', fontWeight: 700 }}>Clear</button>
                <button onClick={saveSig}  disabled={sessionStatus !== 'completed'} style={{ background: sessionStatus === 'completed' ? '#0f172a' : '#e2e8f0', color: sessionStatus === 'completed' ? '#fff' : '#94a3b8', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: sessionStatus === 'completed' ? 'pointer' : 'default', fontWeight: 700 }}>Save Signature</button>
              </div>
              <canvas
                id={canvasId}
                width={600}
                height={200}
                style={{ width: '100%', border: '1px dashed #cbd5e1', borderRadius: 8, touchAction: 'none', opacity: sessionStatus === 'completed' ? 1 : 0.35 }}
              />
              {signMsg && <div className="muted" style={{ marginTop: 6 }}>{signMsg}</div>}
              {signDataUrl && (
                <a className="btn-outline" href={signDataUrl.startsWith('http') ? signDataUrl : `/api${signDataUrl}`} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8 }}>
                  View saved signature
                </a>
              )}
            </div>

            {/* PDI Booklet — available once session is completed */}
            {sessionStatus === 'completed' && (
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginTop: 16 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>PDI Booklet</div>
                <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
                  Generates the full PDI document — cover page, checklist, deficiency summary, and sign-off page. Save as PDF from the print dialog.
                </div>
                <button
                  onClick={() => generatePdiBooklet(sessionId, api.get.bind(api))}
                  style={{ width: '100%', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 10, padding: '16px 0', fontWeight: 800, fontSize: 16, cursor: 'pointer' }}
                >
                  Print / Save PDI Booklet (PDF)
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── deficiency row ───────────────────────────────────────────────────────────
function DefRow({ def, onResolve, onPhoto }) {
  const [note, setNote] = useState(def.resolution_note || '');
  const color = SEV_COLOR[def.severity] || '#6b7280';

  return (
    <div style={{ border: `2px solid ${color}`, borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', marginRight: 8 }}>{def.severity}</span>
          {def.step_id && <span className="muted" style={{ fontSize: 11 }}>Step {def.step_id} · </span>}
          <span style={{ fontWeight: def.status === 'open' ? 700 : 400 }}>{def.description}</span>
          {def.status === 'resolved' && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>✓ {def.resolution_note}</div>}
        </div>
        <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 99, background: def.status === 'resolved' ? '#d1fae5' : '#fee2e2', color: def.status === 'resolved' ? '#065f46' : '#991b1b', fontWeight: 700, flexShrink: 0 }}>
          {def.status}
        </span>
      </div>
      {def.photo_path && (
        <a href={def.photo_path.startsWith('http') ? def.photo_path : `/api${def.photo_path}`} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#3b82f6' }}>View photo</a>
      )}
      {def.status === 'open' && (
        <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#475569' }}>
            📷 Add Photo
            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && onPhoto(def.id, e.target.files[0])} />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="input" style={{ flex: 1 }} placeholder="Resolution note" value={note} onChange={e => setNote(e.target.value)} />
            <button onClick={() => onResolve(def.id, note)} disabled={!note.trim()} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, cursor: note.trim() ? 'pointer' : 'default', opacity: note.trim() ? 1 : 0.5 }}>
              Resolve
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
